import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Loader2, Eye, Users, MousePointerClick, Send, Download, BarChart3,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function KpiCard({ title, value, sub, icon: Icon, accent = "cyan", testid }) {
  const accents = {
    cyan: "from-cyan-500/10 text-cyan-700",
    indigo: "from-indigo-500/10 text-indigo-700",
    emerald: "from-emerald-500/10 text-emerald-700",
    amber: "from-amber-500/10 text-amber-700",
    rose: "from-rose-500/10 text-rose-700",
  }[accent];
  return (
    <div
      data-testid={testid}
      className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden"
    >
      <div className={`absolute inset-0 bg-gradient-to-br to-transparent opacity-60 ${accents.split(" ")[0]} pointer-events-none`} />
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</span>
          <Icon className={`w-4 h-4 ${accents.split(" ")[1]}`} />
        </div>
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function TopList({ title, items, prefix = "", emptyLabel = "No data" }) {
  const max = items?.length ? Math.max(...items.map((i) => i.count)) : 0;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h2 className="text-sm font-semibold text-slate-700 mb-3">{title}</h2>
      {!items || items.length === 0 ? (
        <div className="text-xs text-slate-400 py-4 text-center">{emptyLabel}</div>
      ) : (
        <ul className="space-y-2">
          {items.map((it, idx) => (
            <li key={`${it.key}-${idx}`} className="text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-slate-700 font-medium max-w-[70%]" title={it.key}>
                  {prefix}{it.key || "—"}
                </span>
                <span className="text-xs font-semibold text-slate-500 tabular-nums">{it.count}</span>
              </div>
              <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-cyan-500/70"
                  style={{ width: `${max ? (it.count / max) * 100 : 0}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function todayISO(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

export default function AdminAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dateFrom, setDateFrom] = useState(todayISO(-29));
  const [dateTo, setDateTo] = useState(todayISO(0));

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/analytics`, {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const onExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/exports/analytics.csv`, {
        responseType: "blob",
        params: { date_from: dateFrom, date_to: dateTo },
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `psytech_analytics_${dateFrom}_${dateTo}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Export failed");
    }
  };

  const presetRange = (days) => {
    setDateFrom(todayISO(-(days - 1)));
    setDateTo(todayISO(0));
  };

  const totals = data?.totals;
  const daily = useMemo(() => data?.daily || [], [data]);

  return (
    <div className="space-y-5" data-testid="admin-analytics">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" /> Visitors &amp; Analytics
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Aggregated from the privacy-friendly backend tracker. Plausible runs separately on the public site.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-1 text-sm">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm bg-white"
              data-testid="analytics-date-from"
            />
            <span className="text-slate-400">→</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm bg-white"
              data-testid="analytics-date-to"
            />
          </div>
          <div className="flex gap-1" data-testid="analytics-presets">
            <Button variant="outline" size="sm" onClick={() => presetRange(7)}>7d</Button>
            <Button variant="outline" size="sm" onClick={() => presetRange(30)}>30d</Button>
            <Button variant="outline" size="sm" onClick={() => presetRange(90)}>90d</Button>
          </div>
          <Button variant="outline" size="sm" onClick={onExport} data-testid="analytics-export-csv">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading analytics…
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <KpiCard testid="analytics-kpi-page-views" title="Page views" value={totals.page_views} icon={Eye} accent="cyan" />
            <KpiCard testid="analytics-kpi-unique-visitors" title="Unique visitors" value={totals.unique_visitors} icon={Users} accent="indigo" />
            <KpiCard testid="analytics-kpi-sessions" title="Sessions" value={totals.sessions} icon={Users} accent="emerald" />
            <KpiCard testid="analytics-kpi-button-clicks" title="CTA clicks" value={totals.button_clicks} icon={MousePointerClick} accent="amber" />
            <KpiCard testid="analytics-kpi-form-submits" title="Form submits" value={totals.form_submits} icon={Send} accent="rose" />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Daily traffic</h2>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <AreaChart data={daily.length ? daily : [{ date: dateTo, views: 0, unique_visitors: 0 }]} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grad-views" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0891b2" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#0891b2" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="grad-uv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" name="Page views" dataKey="views" stroke="#0891b2" strokeWidth={2} fill="url(#grad-views)" />
                  <Area type="monotone" name="Unique visitors" dataKey="unique_visitors" stroke="#6366f1" strokeWidth={2} fill="url(#grad-uv)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <TopList title="Top pages" items={data.top_pages} />
            <TopList title="Referrers" items={data.referrers} emptyLabel="No external referrers in this range" />
            <TopList title="UTM sources" items={data.utm_sources} emptyLabel="No tagged campaigns yet" />
            <TopList title="UTM mediums" items={data.utm_mediums} emptyLabel="No tagged campaigns yet" />
            <TopList title="UTM campaigns" items={data.utm_campaigns} emptyLabel="No tagged campaigns yet" />
            <TopList title="Devices" items={data.devices} />
            <TopList title="Browsers" items={data.browsers} />
            <TopList title="Languages" items={data.languages} />
          </div>
        </>
      )}
    </div>
  );
}
